import type { PasswordValidationStatus } from 'firebase/auth';

/** A single password-policy rule and whether the current candidate satisfies it. */
export interface PasswordRequirement {
  key: 'minLength' | 'uppercase' | 'lowercase' | 'number' | 'special';
  /** User-facing checklist label, e.g. "One uppercase letter". */
  label: string;
  /** Whether the current password meets this rule. */
  met: boolean;
}

/** Short phrase used when listing what a password is missing, e.g. "a capital letter". */
const MISSING_PHRASE: Record<PasswordRequirement['key'], string> = {
  minLength: 'enough characters',
  uppercase: 'a capital letter',
  lowercase: 'a lowercase letter',
  number: 'a number',
  special: 'a special character',
};

/**
 * Turn a Firebase {@link PasswordValidationStatus} into an ordered checklist of
 * the rules the project's password policy actually enforces. Reading the live
 * policy keeps the UI in lockstep with the Firebase console — no hardcoded rules
 * to drift out of sync.
 */
export function derivePasswordRequirements(
  status: PasswordValidationStatus,
): PasswordRequirement[] {
  const opts = status.passwordPolicy.customStrengthOptions;
  const requirements: PasswordRequirement[] = [];

  if (opts.minPasswordLength != null) {
    requirements.push({
      key: 'minLength',
      label: `At least ${opts.minPasswordLength} characters`,
      met: status.meetsMinPasswordLength === true,
    });
  }
  if (opts.containsUppercaseLetter) {
    requirements.push({
      key: 'uppercase',
      label: 'One uppercase letter',
      met: status.containsUppercaseLetter === true,
    });
  }
  if (opts.containsLowercaseLetter) {
    requirements.push({
      key: 'lowercase',
      label: 'One lowercase letter',
      met: status.containsLowercaseLetter === true,
    });
  }
  if (opts.containsNumericCharacter) {
    requirements.push({
      key: 'number',
      label: 'One number',
      met: status.containsNumericCharacter === true,
    });
  }
  if (opts.containsNonAlphanumericCharacter) {
    requirements.push({
      key: 'special',
      label: 'One special character',
      met: status.containsNonAlphanumericCharacter === true,
    });
  }

  return requirements;
}

/** Join phrases into a natural-language list: "a", "a and b", "a, b, and c". */
function humanJoin(parts: string[]): string {
  if (parts.length <= 1) return parts.join('');
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
}

/**
 * Build a specific, actionable error message from a requirement checklist, naming
 * exactly what is unmet (e.g. "Your password needs a capital letter and a special
 * character."). `minLength` is used to phrase the length rule. Returns null when
 * every requirement is met.
 */
export function buildMissingMessage(
  requirements: PasswordRequirement[],
  minLength?: number,
): string | null {
  const missing = requirements
    .filter((r) => !r.met)
    .map((r) =>
      r.key === 'minLength' && minLength != null
        ? `at least ${minLength} characters`
        : MISSING_PHRASE[r.key],
    );

  if (missing.length === 0) return null;
  return `Your password needs ${humanJoin(missing)}.`;
}

/**
 * Specific, actionable error message for a Firebase password-validation status, or
 * null when the password satisfies the policy.
 */
export function passwordPolicyErrorMessage(status: PasswordValidationStatus): string | null {
  if (status.isValid) return null;
  return buildMissingMessage(
    derivePasswordRequirements(status),
    status.passwordPolicy.customStrengthOptions.minPasswordLength,
  );
}
